// The REVIEW tab: the template's Commercial Project Review for this version
// — the questions from CRM in their groups, Yes / No and a comment each, the
// figure a question shows beside it, and whether the sell price is unlocked.
// The answers belong to the version, so every option shares them; each is a
// row of its own (review-records.ts). Answers to questions CRM no longer asks
// stay on the version, shown as they were asked.
import type { PricedOption, SectionInput } from '@higgins/m2q-pricing';
import { answeredCount, priceBlockers, questionFigure, REVIEW_ANSWER_OPTIONS, retiredAnswers, type ReviewAnswers } from '@higgins/m2q-pricing';
import type { AnswerChange } from '../lib/review-records';
import type { ReviewQuestionFromApi } from '../lib/types';
import { SavedTextField } from './saved-text-field';

/** The API's limit for a comment (hig_reviewanswer.hig_comment, records.ts in api/). */
const COMMENT_MAX = 4000;

interface Props {
  questions: readonly ReviewQuestionFromApi[];
  answers: ReviewAnswers;
  /** A change to one question's answer, applied to its row as stored now (so quick edits in a row all land). */
  onChange: (question: ReviewQuestionFromApi, change: AnswerChange) => void | Promise<void>;
  sections: readonly SectionInput[];
  priced: PricedOption;
  /** Answers CRM refused (refused.ts), by question key, with the reason given. */
  notSaved: ReadonlyMap<string, string>;
  /** Figures shown beside questions the pricing core doesn't know, by question key (a maintenance option's set-up, #515). */
  figures?: Readonly<Record<string, string>>;
}

export function ReviewTab({ questions, answers, onChange, sections, priced, notSaved, figures = {} }: Props) {
  const groups = [...new Set(questions.map((q) => q.group))];
  const blockers = priceBlockers(questions, answers);
  const retired = retiredAnswers(questions, answers);

  if (!questions.length) {
    return <p className="rounded-lg border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500">CRM has no active review questions.</p>;
  }
  return (
    <div className="space-y-3">
      <p className={`rounded-lg border p-3 text-sm ${blockers.length ? 'border-amber-300 bg-amber-50 text-amber-900' : 'border-green-300 bg-green-50 text-green-900'}`}>
        {answeredCount(questions, answers)} of {questions.length} answered ·{' '}
        {blockers.length ? <>The sell price shows on the Summary once &ldquo;{blockers.map((b) => b.question).join('", "')}&rdquo; is answered Yes.</> : <>The sell price is shown on the Summary.</>} These answers are this version&rsquo;s, shared by every option.
      </p>
      {notSaved.size > 0 && (
        <p className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-900">
          {notSaved.size === 1 ? 'An answer marked “not saved” wasn’t' : `${notSaved.size} answers marked “not saved” weren’t`} saved: {[...notSaved.values()][0]} Discard it on the bar at the top of the job to see CRM&rsquo;s answer.
        </p>
      )}
      {groups.map((group) => (
        <section key={group} className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-xs text-slate-500">
                <th className="px-3 py-1.5 text-left font-semibold text-slate-800">{group}</th>
                <th className="px-2 py-1.5 text-left font-normal" />
                <th className="px-2 py-1.5 text-center font-normal">Yes / No</th>
                <th className="w-2/5 px-2 py-1.5 text-left font-normal">Comments</th>
              </tr>
            </thead>
            <tbody>
              {questions
                .filter((q) => q.group === group)
                .map((q) => {
                  const entry = answers.get(q.key);
                  const figure = figures[q.key] ?? questionFigure(q.shows, sections, priced);
                  return (
                    <tr key={q.key} className="border-t border-slate-100">
                      <td className={`px-3 py-1.5 ${q.gatesPrice ? 'font-medium' : ''}`}>
                        {q.question}
                        {notSaved.has(q.key) && <span className="ml-2 text-xs text-red-700">not saved</span>}
                      </td>
                      <td className="px-2 py-1.5 text-xs whitespace-nowrap text-slate-500">{figure}</td>
                      <td className="px-2 py-1.5">
                        <div className="flex justify-center gap-1" role="radiogroup" aria-label={q.question}>
                          {REVIEW_ANSWER_OPTIONS.map((a) => (
                            <button
                              key={a}
                              type="button"
                              role="radio"
                              aria-checked={entry?.answer === a}
                              onClick={() => onChange(q, { answer: entry?.answer === a ? null : a })}
                              className={`h-7 w-11 rounded-md border text-sm ${entry?.answer === a ? 'border-primary bg-orange-50 font-medium text-slate-900' : 'border-slate-300 text-slate-500 hover:border-slate-400'}`}
                            >
                              {a}
                            </button>
                          ))}
                        </div>
                      </td>
                      <td className="px-2 py-1.5">
                        <SavedTextField
                          compact
                          value={entry?.comment ?? ''}
                          onSave={(comment) => onChange(q, { comment })}
                          maxLength={COMMENT_MAX}
                          label={`${q.question} — comment`}
                        />
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </section>
      ))}
      {retired.length > 0 && (
        <section className="rounded-lg border border-slate-200 bg-white">
          <h3 className="bg-slate-50 px-3 py-1.5 text-sm font-semibold text-slate-800">No longer asked</h3>
          <p className="px-3 pt-1.5 text-xs text-slate-500">Answered on this version before CRM stopped asking; kept as they were, and not counted above.</p>
          <ul className="divide-y divide-slate-100 text-sm">
            {retired.map((a) => (
              <li key={a.key} className="flex flex-wrap gap-x-3 px-3 py-1.5">
                <span>{a.questionAsked ?? a.key}</span>
                <span className="font-medium">{a.answer ?? 'Not answered'}</span>
                {a.comment && <span className="text-slate-500">{a.comment}</span>}
              </li>
            ))}
          </ul>
        </section>
      )}
      <p className="text-xs text-slate-500">Tap an answer again to clear it. The template&rsquo;s signature block isn&rsquo;t here: the day of the last change is saved on the version (reviewed on).</p>
    </div>
  );
}
